import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import categoryService from '../services/categoryService'

const CategoryShowcase = () => {
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate()
    
    const imageApiUrl = process.env.REACT_APP_IMAGE_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5196'

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true)
                const response = await categoryService.getMainCategories()
                if (response && response.succeeded) {
                    // DisplayOrder'a göre sırala
                    const sortedCategories = [...(response.categories || [])].sort((a, b) => {
                        if (a.displayOrder !== b.displayOrder) {
                            return a.displayOrder - b.displayOrder
                        }
                        return a.name.localeCompare(b.name, 'tr')
                    })
                    setCategories(sortedCategories)
                }
            } catch (err) {
                console.error('Kategoriler yüklenemedi:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchCategories()
    }, [])

    const handleCategoryClick = (categoryId) => {
        navigate(`/shop?categoryId=${categoryId}`)
    }

    if (loading) {
        return (
            <section className="category-showcase py-80">
                <div className="container container-lg">
                    <div className="text-center py-5">
                        <div className="spinner-border text-main-600" role="status">
                            <span className="visually-hidden">Yükleniyor...</span>
                        </div>
                    </div>
                </div>
            </section>
        )
    }

    if (!categories || categories.length === 0) {
        return null
    }

    return (
        <section className="category-showcase py-80">
            <div className="container container-lg">
                <div className="section-heading text-center mb-48">
                    <h2 className="mb-12">Ürün Kategorileri</h2>
                    <p className="text-neutral-600 mb-0">Gümüş pestil çeşitlerimizi keşfedin</p>
                </div>

                <div className="row g-24">
                    {categories.map((category) => (
                        <div key={category.id} className="col-lg-4 col-md-6 col-12">
                            <div 
                                className="category-card position-relative rounded-24 overflow-hidden cursor-pointer"
                                onClick={() => handleCategoryClick(category.id)}
                                style={{ 
                                    height: 'clamp(220px, 35vw, 280px)', // responsive height
                                    cursor: 'pointer'
                                }}
                            >
                                {/* Background Image */}
                                <div className="category-card__bg position-absolute w-100 h-100">
                                    {category.imageUrl ? (
                                        <img
                                            src={
                                                category.imageUrl.startsWith('http')
                                                    ? category.imageUrl
                                                    : `${imageApiUrl.replace(/\/api\/?$/,'')}${category.imageUrl}`
                                            }
                                            alt={category.name}
                                            className="w-100 h-100 object-fit-cover"
                                            onError={(e) => {
                                                e.target.onerror = null
                                                e.target.style.display = 'none'
                                                e.target.parentElement.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                            }}
                                        />
                                    ) : (
                                        <div 
                                            className="w-100 h-100"
                                            style={{ 
                                                background: `linear-gradient(135deg, ${getCategoryGradient(category.id)})` 
                                            }}
                                        />
                                    )}
                                    {/* Overlay */}
                                    <div 
                                        className="position-absolute w-100 h-100 inset-block-start-0 inset-inline-start-0 transition-2"
                                        style={{
                                            background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)'
                                        }}
                                    />
                                </div>

                                {/* Content */}
                                <div className="category-card__content position-absolute w-100 h-100 inset-block-start-0 inset-inline-start-0 d-flex flex-column justify-content-end p-32">
                                    <h3 className="text-white mb-8 fw-bold" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}>
                                        {category.name}
                                    </h3>
                                    {category.description && (
                                        <p className="text-white mb-16 opacity-90" style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                                            {category.description}
                                        </p>
                                    )}
                                    <div className="d-flex align-items-center gap-8">
                                        <span className="text-white fw-semibold">
                                            Ürünleri İncele
                                        </span>
                                        <i className="ph ph-arrow-right text-white text-xl"></i>
                                    </div>
                                </div>

                                {/* Hover Effect Overlay */}
                                <div 
                                    className="category-card__hover-overlay position-absolute w-100 h-100 inset-block-start-0 inset-inline-start-0 transition-2"
                                    style={{
                                        background: 'rgba(41, 158, 96, 0.2)',
                                        opacity: 0
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <style jsx>{`
                .category-card {
                    transition: all 0.4s ease;
                }
                .category-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
                }
                .category-card:hover .category-card__hover-overlay {
                    opacity: 1 !important;
                }
                .category-card:hover .category-card__bg img {
                    transform: scale(1.1);
                }
                .category-card__bg img {
                    transition: transform 0.4s ease;
                }
            `}</style>
        </section>
    )
}

// Kategori ID'sine göre gradient renkleri
const getCategoryGradient = (categoryId) => {
    const gradients = [
        '#667eea 0%, #764ba2 100%',
        '#f093fb 0%, #f5576c 100%',
        '#4facfe 0%, #00f2fe 100%',
        '#43e97b 0%, #38f9d7 100%',
        '#fa709a 0%, #fee140 100%',
        '#30cfd0 0%, #330867 100%',
    ]
    return gradients[categoryId % gradients.length]
}

export default CategoryShowcase

